import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import CookieBanner from "../components/CookieBanner";
import {
  clearPersistentFunctionalStorage,
  normalizeConsent,
  readStoredConsent,
  storeConsent,
} from "../lib/consent";

import { setAnalyticsConsent } from "../lib/analyticsClient.js";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [storedConsent, setStoredConsent] = useState(null);
  const [ready, setReady] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    setStoredConsent(stored);
    setAnalyticsConsent(stored?.analytics === true);
    setReady(true);
  }, []);

  const applyConsent = useCallback(
    (nextConsent) => {
      const normalized = normalizeConsent(nextConsent);

      if (!normalized.functional) {
        clearPersistentFunctionalStorage();
      }

      storeConsent(normalized);
      setAnalyticsConsent(normalized.analytics === true);
      setStoredConsent(normalized);
      setPreferencesOpen(false);
    },
    [storedConsent]
  );

  const acceptAll = useCallback(() => {
    applyConsent({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: false,
    });
  }, [applyConsent]);

  const denyOptional = useCallback(() => {
    applyConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  }, [applyConsent]);

  const savePreferences = useCallback(
    (preferences) => {
      applyConsent({
        necessary: true,
        functional: !!preferences?.functional,
        analytics: !!preferences?.analytics,
        marketing: !!preferences?.marketing,
      });
    },
    [applyConsent]
  );

  const value = useMemo(() => {
    const consent = normalizeConsent(storedConsent);
    return {
      consent,
      ready,
      bannerVisible: ready && !storedConsent,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      acceptAll,
      denyOptional,
      savePreferences,
      hasConsent: (category) => consent[category] === true,
    };
  }, [acceptAll, denyOptional, preferencesOpen, ready, savePreferences, storedConsent]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieBanner />
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent måste användas inom CookieConsentProvider");
  }
  return context;
}
